import React, { useEffect, useState } from 'react';
import { CompactPeoplePicker, IBasePickerSuggestionsProps, IPersonaProps, Spinner, SpinnerSize, Stack, StackItem } from '@fluentui/react';
import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';
import { IPeoplePickerSearchResult, IUserConfigPackage } from '../../API';
import API from '../../API';

export enum PeoplePickerIdFormat {
	UPN,
	EntraID
}

export class NewPeoplePickerState {
	
	config: IUserConfigPackage;
	initialPeople: string[];
	updatePeople: (people: string[]) => void;
	siteUrl: string;

	format: PeoplePickerIdFormat = PeoplePickerIdFormat.UPN;

	loading: boolean = false;
	loaded: boolean = false;

	groupSplitterValue:string = '|:g:|';

	constructor(siteUrl:string, config:IUserConfigPackage, initialPeople:string[], format: PeoplePickerIdFormat, updatePeople: (people:string[]) => void) {
		this.siteUrl = siteUrl;
		this.config = config;
		this.initialPeople = initialPeople;
		this.format = format;
		this.updatePeople = updatePeople;

		makeAutoObservable(this);
	}
	
	async loadInitialPeople() {
		if(!this.loading) {
			this.loading = true;
			var initPeople:IPersonaProps[] = [];
			var initPeopleSearch: string[] = [];

			for (let index = 0; index < this.initialPeople.length; index++) {
				const element = this.initialPeople[index];

				//If it's one of our annoying encoded group UPNs, just add it as-is.
				if(element.indexOf(this.groupSplitterValue) > 0) {
					initPeople.push({ text: element.split(this.groupSplitterValue)[0], key: element } as IPersonaProps);
				} else {
					initPeopleSearch.push(element);
				}
			}
			

			let searchResults = initPeopleSearch.length > 0 ? await this.getPeopleSearchResults(initPeopleSearch) : [];
			initPeople = initPeople.concat(searchResults);
			this.setDefaultPeopleList(initPeople);
			this.setLoaded(true);
		}
	}

	setLoaded(loaded: boolean) {
		this.loaded = loaded;
	}

	public defaultPeopleList:IPersonaProps[] = [];
	public setDefaultPeopleList(people:IPersonaProps[]) {
		this.defaultPeopleList = people;
	}

	public async onFilterChanged(filterText: string, currentPersonas?: IPersonaProps[], limitResults?: number,): Promise<IPersonaProps[]> {
		if (filterText) {

			let filteredPersonas: IPersonaProps[] = await this.getPeopleSearchResults([filterText]);

			filteredPersonas = this.removeDuplicates(filteredPersonas, currentPersonas || []);
			filteredPersonas = limitResults ? filteredPersonas.slice(0, limitResults) : filteredPersonas;
			return filteredPersonas;

		} else {
			return [];
		}
	}

	private async getPeopleSearchResults(filterText: string[]) : Promise<IPersonaProps[]> {
		let api = new API(await this.config.getToken());
		let results = await api.searchPeople(this.siteUrl, filterText);

		return this.processPeopleResults(results, this.format);
	}

	private processPeopleResults(results: IPeoplePickerSearchResult[], format:PeoplePickerIdFormat) {
		let filteredPersonas: IPersonaProps[] = results.map((r: any) => {

			//Really annoyingly, group UPNs don't resolve against the people picker service. So when we reload the form, we need a way to get the name of what we've selected. Do that by storing it in this key.
			let key = r.EntityType === 'User' ? r.Key : (r.DisplayText + this.groupSplitterValue + r.Key);

			if(r.EntityType === 'User' && format === PeoplePickerIdFormat.EntraID && r.EntityData?.ObjectId) {
				key = r.EntityData.ObjectId;
			}

			return { text: r.DisplayText, secondaryText: r.EntityData?.Email || r.Key, key: key } as IPersonaProps;
		});
		return filteredPersonas;
	}

	private removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[]) {
		return personas.filter(persona => !this.listContainsPersona(persona, possibleDupes));
	}

	private listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
		if (!personas || !personas.length || personas.length === 0) {
			return false;
		}
		return personas.filter(item => item.text === persona.text).length > 0;
	}
	
}

export interface INewPeoplePickerProps {
	format:PeoplePickerIdFormat;
	config:IUserConfigPackage;
	siteUrl:string;
	initialPeople:string[];
	updatePeople: (people:string[]) => void;
}

function NewPeoplePicker(props:INewPeoplePickerProps) {

	let siteUrl = props.siteUrl ? props.siteUrl : props.config.tenantUrl;

	const [state] = useState(() => {
		let result = new NewPeoplePickerState(
			siteUrl,
			props.config,
			props.initialPeople,
			props.format,
			props.updatePeople
		);
		return result;
	});
	
	useEffect(() => {
		state.loadInitialPeople();
	}, [state]);

	let suggestionProps: IBasePickerSuggestionsProps = {
		suggestionsHeaderText: 'Suggested People',
		mostRecentlyUsedHeaderText: 'Suggested Contacts',
		noResultsFoundText: 'No results found',
		loadingText: 'Loading',
		showRemoveButtons: true,
		suggestionsAvailableAlertText: 'People Picker Suggestions available',
		suggestionsContainerAriaLabel: 'Suggested contacts',
	};

	return (
		<Stack horizontal grow>
			<StackItem grow>
				<CompactPeoplePicker
					key={'CompactPeoplePicker' + state.defaultPeopleList.length}
					// eslint-disable-next-line react/jsx-no-bind
					onResolveSuggestions={state.onFilterChanged.bind(state)}
					defaultSelectedItems={state.defaultPeopleList}
					getTextFromItem={persona => persona.text as string}
					pickerSuggestionsProps={suggestionProps}
					selectionAriaLabel={'Selected contacts'}
					removeButtonAriaLabel={'Remove'}
					className={'ms-PeoplePicker'}
					// eslint-disable-next-line react/jsx-no-bind
					onChange={(items?: IPersonaProps[]) => {
						props.updatePeople((items || []).map(i => (i.key as string)  || ''));
					}}
					inputProps={{
						'aria-label': 'People Picker',
					}}
					resolveDelay={300}
					disabled={state.loading && !state.loaded}
				/>
			</StackItem>
			<StackItem>
			{state.loading && !state.loaded &&
				<Spinner size={SpinnerSize.small} label="Loading people..." labelPosition="right" />
			}
			</StackItem>
		</Stack>
	);
}

export default observer(NewPeoplePicker);
